import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Switch,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ExitToApp as ExitIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { api } from '../api/chat';
import {
  refreshBlocked,
  refreshContacts,
  refreshConversations,
  setActiveConversation,
  setView,
  setRightContent,
  removeConversation,
  loadMessages,
} from '../redux/chat.redux';
import type { Conversation } from '../types/message';
import type { GroupMember } from '../types/people';
import { avatarText, formatFullTime } from '../utils/format';

// ---------- 私聊设置 ----------
interface PrivateProps {
  conversation: Conversation;
  peer: any; // User
  peerBlocked: boolean;
  onClose: () => void;
}

export function PrivateSettingsPanel({ conversation, peer, peerBlocked, onClose }: PrivateProps) {
  const dispatch = useAppDispatch();
  const [muted, setMuted] = useState(conversation.muted_self || false);
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const toggleMute = async (v: boolean) => {
    setMuted(v);
    try {
      await api.muteSelf(conversation.id, v);
      dispatch(refreshConversations());
    } catch (err: any) {
      setMuted(!v);
      alert(err?.message || '操作失败');
    }
  };

  const doSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const r = await api.searchMessages(conversation.id, searchQ.trim());
      setResults(r);
    } catch (err: any) {
      alert(err?.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  };

  const blockToggle = async () => {
    try {
      if (peerBlocked) {
        await api.unblockUser(peer.id);
      } else {
        await api.blockUser(peer.id);
      }
      dispatch(refreshBlocked());
      if (!peerBlocked) {
        // 拉黑后清除当前会话
        dispatch(removeConversation(conversation.id));
        dispatch(setActiveConversation(null));
        onClose();
      }
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const deleteContact = async () => {
    if (!window.confirm(`确定删除好友 ${peer.nickname || peer.username} 吗?删除后对方不再出现在你的通讯录,聊天记录将被隐藏。`)) {
      return;
    }
    try {
      await api.deleteContact(peer.id);
      dispatch(refreshContacts());
      dispatch(refreshConversations());
      dispatch(removeConversation(conversation.id));
      dispatch(setActiveConversation(null));
      onClose();
    } catch (err: any) {
      alert(err?.message || '删除失败');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
        <Avatar src={peer?.avatar || undefined} sx={{ width: 64, height: 64, bgcolor: 'primary.light', fontSize: 26 }}>
          {avatarText(peer)}
        </Avatar>
        <Typography sx={{ mt: 1, fontWeight: 600 }}>{peer?.nickname || peer?.username}</Typography>
        <Typography variant="caption" color="text.secondary">
          {peer?.username}
        </Typography>
      </Box>
      <Divider />
      <List disablePadding>
        <ListItem sx={{ px: 0 }}>
          <ListItemText primary="消息免打扰" />
          <Switch checked={muted} onChange={(e) => toggleMute(e.target.checked)} size="small" />
        </ListItem>
      </List>
      <Divider />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        查找聊天记录
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="搜索关键词"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
        />
        <IconButton onClick={doSearch} disabled={searching || !searchQ.trim()}>
          {searching ? <CircularProgress size={18} /> : <SearchIcon />}
        </IconButton>
      </Box>
      {results.length > 0 && (
        <List dense>
          {results.map((m) => (
            <ListItem key={m.id} sx={{ px: 0 }}>
              <ListItemText
                primary={<Typography variant="body2" noWrap>{m.content}</Typography>}
                secondary={formatFullTime(m.created_at)}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItem>
          ))}
        </List>
      )}
      <Divider sx={{ my: 2 }} />
      <Button fullWidth startIcon={<BlockIcon />} color={peerBlocked ? 'success' : 'error'} variant="outlined" sx={{ mb: 1 }} onClick={blockToggle}>
        {peerBlocked ? '解除拉黑' : '加入黑名单'}
      </Button>
      <Button fullWidth startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={deleteContact}>
        删除好友
      </Button>
    </Box>
  );
}

// ---------- 群聊设置 ----------
interface GroupProps {
  conversation: Conversation;
  onClose: () => void;
}

export function GroupSettingsPanel({ conversation, onClose }: GroupProps) {
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.user.user);
  const contacts = useAppSelector((s) => s.chat.contacts);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(conversation.muted_self || false);
  const [groupMuted, setGroupMuted] = useState(conversation.muted || false);
  const [rename, setRename] = useState(conversation.name || '');
  const [editingName, setEditingName] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [memberMenu, setMemberMenu] = useState<{ anchor: HTMLElement; member: GroupMember } | null>(null);

  const isOwner = conversation.owner_id === me?.id;
  const isGroup = conversation.type === 'group';

  const loadMembers = async () => {
    setLoading(true);
    try {
      const r = await api.conversationMembers(conversation.id);
      setMembers(r);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isGroup) loadMembers();
    // eslint-disable-next-line
  }, [conversation.id]);

  if (!isGroup) return null;

  const toggleMute = async (v: boolean) => {
    setMuted(v);
    try {
      await api.muteSelf(conversation.id, v);
      dispatch(refreshConversations());
    } catch (err: any) {
      setMuted(!v);
    }
  };

  const toggleGroupMute = async (v: boolean) => {
    setGroupMuted(v);
    try {
      await api.muteGroup(conversation.id, v);
      dispatch(refreshConversations());
    } catch (err: any) {
      setGroupMuted(!v);
    }
  };

  const saveName = async () => {
    if (!rename.trim()) return;
    try {
      await api.updateGroup(conversation.id, rename.trim());
      setEditingName(false);
      dispatch(refreshConversations());
    } catch (err: any) {
      alert(err?.message || '修改失败');
    }
  };

  const addMembers = async (ids: number[]) => {
    try {
      for (const id of ids) {
        await api.addMember(conversation.id, id);
      }
      setAddOpen(false);
      dispatch(refreshConversations());
      loadMembers();
    } catch (err: any) {
      alert(err?.message || '添加失败');
    }
  };

  const removeMember = async (uid: number) => {
    try {
      await api.removeMember(conversation.id, uid);
      loadMembers();
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const leaveGroup = async () => {
    if (!window.confirm('确定退出该群聊吗?')) return;
    try {
      await api.removeMember(conversation.id, me!.id);
      dispatch(refreshConversations());
      dispatch(removeConversation(conversation.id));
      dispatch(setActiveConversation(null));
      onClose();
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const dissolve = async () => {
    if (!window.confirm('解散群聊后所有成员将无法访问,确定解散吗?')) return;
    try {
      await api.dissolveGroup(conversation.id);
      dispatch(refreshConversations());
      dispatch(removeConversation(conversation.id));
      dispatch(setActiveConversation(null));
      onClose();
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const transfer = async (uid: number) => {
    try {
      await api.transferGroup(conversation.id, uid);
      setTransferOpen(false);
      dispatch(refreshConversations());
      loadMembers();
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const openPrivateWith = async (uid: number) => {
    try {
      const conv = await api.openPrivate(uid);
      dispatch(refreshConversations());
      dispatch(setActiveConversation(conv.id));
      dispatch(loadMessages(conv.id));
      dispatch(setView('conversations'));
      dispatch(setRightContent({ type: 'chat' }));
    } catch (err: any) {
      alert(err?.message || '无法发起私聊');
    }
  };

  const addFriend = async (uid: number) => {
    try {
      const r = await api.addContact({ user_id: uid });
      if (r.auto_accepted) {
        alert('已添加好友');
        dispatch(refreshContacts());
      } else {
        alert('好友申请已发送');
        dispatch(refreshContacts());
      }
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const notInGroupContacts = contacts.filter((c) => !members.some((m) => m.user.id === c.id));

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
        <Avatar src={conversation.avatar || undefined} sx={{ width: 64, height: 64, bgcolor: 'primary.light', fontSize: 26 }}>
          {avatarText({ nickname: conversation.name, username: conversation.name })}
        </Avatar>
        {editingName ? (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, width: '100%' }}>
            <TextField size="small" value={rename} onChange={(e) => setRename(e.target.value)} />
            <Button size="small" variant="contained" onClick={saveName}>
              保存
            </Button>
          </Box>
        ) : (
          <Typography sx={{ mt: 1, fontWeight: 600 }} onClick={() => isOwner && setEditingName(true)}>
            {conversation.name || conversation.display_name}
            {isOwner && (
              <Tooltip title="点击修改群名称">
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          共 {members.length} 名成员
        </Typography>
      </Box>
      <Divider />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        群成员
      </Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <List disablePadding dense>
          {members.map((m) => (
            <ListItem
              key={m.user.id}
              sx={{ px: 0 }}
              secondaryAction={
                <>
                  {isOwner && m.user.id !== me?.id && (
                    <IconButton
                      size="small"
                      onClick={(e) => setMemberMenu({ anchor: e.currentTarget, member: m })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              }
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar src={m.user.avatar || undefined} sx={{ width: 34, height: 34, bgcolor: 'primary.light', fontSize: 14 }}>
                  {avatarText(m.user)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" noWrap>
                      {m.user.nickname || m.user.username}
                    </Typography>
                    {m.role === 'owner' && (
                      <Typography variant="caption" sx={{ color: 'warning.main' }}>
                        群主
                      </Typography>
                    )}
                    {m.user.id === me?.id && (
                      <Typography variant="caption" color="text.secondary">
                        我
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
          <ListItem sx={{ px: 0 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={notInGroupContacts.length === 0}>
              添加成员
            </Button>
          </ListItem>
        </List>
      )}
      <Divider sx={{ my: 1.5 }} />
      <List disablePadding>
        <ListItem sx={{ px: 0 }}>
          <ListItemText primary="消息免打扰" />
          <Switch checked={muted} onChange={(e) => toggleMute(e.target.checked)} size="small" />
        </ListItem>
        {isOwner && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="全员禁言(仅群主可发言)" />
            <Switch checked={groupMuted} onChange={(e) => toggleGroupMute(e.target.checked)} size="small" />
          </ListItem>
        )}
      </List>
      <Divider sx={{ mb: 1.5 }} />
      {isOwner ? (
        <>
          <Button fullWidth variant="outlined" sx={{ mb: 1 }} onClick={() => setTransferOpen(true)}>
            转让群主
          </Button>
          <Button fullWidth variant="outlined" color="error" sx={{ mb: 1 }} onClick={dissolve}>
            解散群聊
          </Button>
        </>
      ) : (
        <Button fullWidth variant="outlined" color="error" startIcon={<ExitIcon />} onClick={leaveGroup}>
          退出群聊
        </Button>
      )}

      {/* 成员操作菜单 */}
      <Menu
        anchorEl={memberMenu?.anchor || null}
        open={Boolean(memberMenu)}
        onClose={() => setMemberMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (memberMenu) openPrivateWith(memberMenu.member.user.id);
            setMemberMenu(null);
          }}
        >
          发私信
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (memberMenu) addFriend(memberMenu.member.user.id);
            setMemberMenu(null);
          }}
        >
          添加好友
        </MenuItem>
        {isOwner && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              if (memberMenu) removeMember(memberMenu.member.user.id);
              setMemberMenu(null);
            }}
          >
            移出群聊
          </MenuItem>
        )}
      </Menu>

      {/* 添加成员对话框 */}
      <AddMemberDialog
        open={addOpen}
        candidates={notInGroupContacts}
        onCancel={() => setAddOpen(false)}
        onConfirm={addMembers}
      />

      {/* 转让群主对话框 */}
      <TransferDialog open={transferOpen} members={members} meId={me?.id || 0} onCancel={() => setTransferOpen(false)} onConfirm={transfer} />
    </Box>
  );
}

// ---------- 添加成员 ----------
function AddMemberDialog(props: {
  open: boolean;
  candidates: any[];
  onCancel: () => void;
  onConfirm: (ids: number[]) => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  React.useEffect(() => {
    if (props.open) setSelected([]);
  }, [props.open]);

  const toggle = (id: number) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open={props.open} onClose={props.onCancel} fullWidth maxWidth="xs">
      <DialogTitle>添加成员</DialogTitle>
      <DialogContent>
        {props.candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            没有可添加的好友
          </Typography>
        )}
        <List disablePadding>
          {props.candidates.map((u) => (
            <ListItem key={u.id} disablePadding sx={{ px: 1 }}>
              <Checkbox checked={selected.includes(u.id)} onChange={() => toggle(u.id)} size="small" />
              <ListItemAvatar>
                <Avatar src={u.avatar || undefined}>{avatarText(u)}</Avatar>
              </ListItemAvatar>
              <ListItemText primary={u.nickname || u.username} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>取消</Button>
        <Button variant="contained" disabled={selected.length === 0} onClick={() => props.onConfirm(selected)}>
          添加({selected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- 转让群主 ----------
function TransferDialog(props: {
  open: boolean;
  members: GroupMember[];
  meId: number;
  onCancel: () => void;
  onConfirm: (uid: number) => void;
}) {
  return (
    <Dialog open={props.open} onClose={props.onCancel} fullWidth maxWidth="xs">
      <DialogTitle>转让群主</DialogTitle>
      <DialogContent>
        <List disablePadding>
          {props.members
            .filter((m) => m.user.id !== props.meId)
            .map((m) => (
              <ListItem key={m.user.id} disablePadding>
                <Button fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => props.onConfirm(m.user.id)}>
                  <ListItemAvatar>
                    <Avatar src={m.user.avatar || undefined}>{avatarText(m.user)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={m.user.nickname || m.user.username} />
                </Button>
              </ListItem>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>取消</Button>
      </DialogActions>
    </Dialog>
  );
}
